import { useId, useState } from 'react';

/**
 * Nationals trip budget: turns a trip into a total and into "how many
 * supporters at $X a month" — the number a family can actually put on a fund
 * page. Defaults are deliberately round and editable; they are starting
 * points, not estimates for any particular event.
 */
const FIELDS = [
  { key: 'travelers', label: 'Travelers (athlete + family)', step: 1, min: 1 },
  { key: 'flight', label: 'Airfare or gas, per traveler ($)', step: 25, min: 0 },
  { key: 'nights', label: 'Hotel nights', step: 1, min: 0 },
  { key: 'hotel', label: 'Hotel per night ($)', step: 10, min: 0 },
  { key: 'meals', label: 'Food per traveler per day ($)', step: 5, min: 0 },
  { key: 'entry', label: 'Entry / registration fees ($)', step: 25, min: 0 },
  { key: 'other', label: 'Other: rental car, gear, uniform ($)', step: 25, min: 0 },
] as const;

type Key = (typeof FIELDS)[number]['key'];

const DEFAULTS: Record<Key, number> = {
  travelers: 2,
  flight: 350,
  nights: 4,
  hotel: 160,
  meals: 45,
  entry: 250,
  other: 200,
};

const usd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function tripTotal(v: Record<Key, number>): number {
  const days = v.nights + 1;
  return v.travelers * v.flight + v.nights * v.hotel + v.travelers * v.meals * days + v.entry + v.other;
}

export function TripBudgetCalculator() {
  const id = useId();
  const [values, setValues] = useState(DEFAULTS);
  const [gift, setGift] = useState(25);
  const [months, setMonths] = useState(3);

  const total = tripTotal(values);
  const monthlySupporters = Math.max(1, Math.ceil(total / (Math.max(gift, 1) * Math.max(months, 1))));
  const oneTimeAt50 = Math.ceil(total / 50);

  const set = (key: Key, raw: string) => {
    const n = Number(raw);
    setValues((prev) => ({ ...prev, [key]: Number.isFinite(n) && n >= 0 ? n : 0 }));
  };

  return (
    <div className="not-prose mt-6 rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-8">
      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <label key={f.key} htmlFor={`${id}-${f.key}`} className="text-sm font-medium text-slate-700">
            {f.label}
            <input
              id={`${id}-${f.key}`}
              type="number"
              inputMode="numeric"
              min={f.min}
              step={f.step}
              value={values[f.key]}
              onChange={(e) => set(f.key, e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900"
            />
          </label>
        ))}
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5">
        <p className="text-sm text-slate-500">Estimated trip cost</p>
        <p className="text-4xl font-extrabold tracking-tight text-slate-900" aria-live="polite">
          {usd(total)}
        </p>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <label htmlFor={`${id}-gift`} className="text-sm font-medium text-slate-700">
          Monthly gift per supporter ($)
          <input
            id={`${id}-gift`}
            type="number"
            min={1}
            step={5}
            value={gift}
            onChange={(e) => setGift(Math.max(1, Number(e.target.value) || 1))}
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900"
          />
        </label>
        <label htmlFor={`${id}-months`} className="text-sm font-medium text-slate-700">
          Months until the trip
          <input
            id={`${id}-months`}
            type="number"
            min={1}
            max={24}
            step={1}
            value={months}
            onChange={(e) => setMonths(Math.min(24, Math.max(1, Number(e.target.value) || 1)))}
            className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900"
          />
        </label>
      </div>

      <p className="mt-5 text-slate-700 leading-relaxed" aria-live="polite">
        That's <strong>{monthlySupporters} supporters</strong> giving <strong>{usd(gift)}/month</strong> for{' '}
        {months} {months === 1 ? 'month' : 'months'} — or <strong>{oneTimeAt50} one-time gifts of $50</strong>. Most
        athletes reach that many people through grandparents, aunts and uncles, family friends and a coach or two.
      </p>
    </div>
  );
}
